import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { SMMLogo } from './pages/Landing'

function ScrollToTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      // Small delay to let the page render before scrolling to the anchor
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1))
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } else {
      window.scrollTo(0, 0)
    }
  }, [pathname, hash])

  return null
}

function PageLoader() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <SMMLogo className="w-12 h-12 mx-auto mb-4 animate-pulse" />
        <p className="text-slate-400">Loading...</p>
      </div>
    </div>
  )
}

// Eager import for landing page (first page users see)
import Landing from './pages/Landing'

// Lazy imports for all other pages
const Features = lazy(() => import('./pages/Features'))
const BetaSignup = lazy(() => import('./pages/BetaSignup'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Admin = lazy(() => import('./pages/Admin'))
const ResellerDashboard = lazy(() => import('./pages/ResellerDashboard'))
const ResellerProgramme = lazy(() => import('./pages/ResellerProgramme'))
const AgreementPrint = lazy(() => import('./pages/AgreementPrint'))
const Settings = lazy(() => import('./pages/Settings'))
const ContentStudio = lazy(() => import('./pages/ContentStudio'))
const ContentLibrary = lazy(() => import('./pages/ContentLibrary'))
const BrandSetup = lazy(() => import('./pages/BrandSetup'))
const BrandDashboard = lazy(() => import('./pages/BrandDashboard'))
const BrandSettings = lazy(() => import('./pages/BrandSettings'))
const BrandAssets = lazy(() => import('./pages/BrandAssets'))
const SocialConnections = lazy(() => import('./pages/SocialConnections'))
const BlogEditor = lazy(() => import('./pages/BlogEditor'))
const RepurposeView = lazy(() => import('./pages/RepurposeView'))
const CommentManager = lazy(() => import('./pages/CommentManager'))
const IdeaBacklog = lazy(() => import('./pages/IdeaBacklog'))
const CampaignList = lazy(() => import('./pages/CampaignList'))
const CampaignPlanner = lazy(() => import('./pages/CampaignPlanner'))
const CampaignDashboard = lazy(() => import('./pages/CampaignDashboard'))
const BrandList = lazy(() => import('./pages/BrandList'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Guides = lazy(() => import('./pages/Guides'))
const PreviewActivity = lazy(() => import('./pages/PreviewActivity'))
const Support = lazy(() => import('./pages/Support'))

export default function App() {
  return (
    <>
    <ScrollToTop />
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/beta" element={<BetaSignup />} />
      <Route path="/partners" element={<ResellerProgramme />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/reseller" element={<ResellerDashboard />} />
      <Route path="/agreement/print" element={<AgreementPrint />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/content" element={<ContentLibrary />} />
      <Route path="/content/create" element={<ContentStudio />} />
      <Route path="/content/blog" element={<BlogEditor />} />
      <Route path="/content/:id/repurpose" element={<RepurposeView />} />
      <Route path="/brands" element={<BrandList />} />
      <Route path="/brands/new" element={<BrandSetup />} />
      <Route path="/brands/:id" element={<BrandDashboard />} />
      <Route path="/brands/:id/settings" element={<BrandSettings />} />
      <Route path="/brands/:id/assets" element={<BrandAssets />} />
      <Route path="/connections" element={<SocialConnections />} />
      <Route path="/guides" element={<Guides />} />
      <Route path="/preview-activity" element={<PreviewActivity />} />
      <Route path="/support" element={<Support />} />
      <Route path="/support/:ticketId" element={<Support />} />

      <Route path="/engage" element={<CommentManager />} />
      <Route path="/ideas" element={<IdeaBacklog />} />
      <Route path="/campaigns" element={<CampaignList />} />
      <Route path="/campaigns/new" element={<CampaignPlanner />} />
      <Route path="/campaigns/:id" element={<CampaignDashboard />} />

      {/* Catch-all: avoids a blank screen on typos or stale links. */}
      <Route path="*" element={
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <SMMLogo className="w-12 h-12 mx-auto mb-6 opacity-60" />
            <h1 className="text-2xl font-bold text-white mb-2">Page not found</h1>
            <p className="text-slate-400 mb-6">The URL you followed doesn't match anything on Upcast. It may have been moved or renamed.</p>
            <a href="/dashboard" className="inline-block bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-medium px-6 py-3 rounded-lg transition-all">
              Go to Dashboard
            </a>
          </div>
        </div>
      } />
    </Routes>
    </Suspense>
    </>
  )
}
